// src/hooks/useDefaultVoices.ts
/**
 * useDefaultVoices Hook
 *
 * Provides access to ElevenLabs pre-made default voices.
 * These voices are available immediately without requiring
 * users to clone their own voice.
 *
 * React 19 patterns:
 * - useMemo for filtered voice lists
 * - useCallback for filter/selection handlers
 */

import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_VOICES, getDefaultVoiceById, isDefaultVoice } from '@/constants/defaultVoices';
import type { IDefaultVoice, DefaultVoiceUseCase } from '@/@types/voices';

// ============================================================================
// Default Filter State
// ============================================================================

const DEFAULT_FILTERS: {
  gender?: 'male' | 'female' | 'all';
  useCase?: DefaultVoiceUseCase | 'all';
  searchQuery?: string;
} = {
  gender: 'all',
  useCase: 'all',
  searchQuery: '',
};

// ============================================================================
// Hook
// ============================================================================

export function useDefaultVoices() {
  // State for selected voice and filters
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);

  // ============================================================================
  // Computed Values
  // ============================================================================

  const femaleVoices = useMemo(() => DEFAULT_VOICES.filter(v => v.gender === 'female'), []);

  const maleVoices = useMemo(() => DEFAULT_VOICES.filter(v => v.gender === 'male'), []);

  const filteredVoices = useMemo(() => {
    let result = [...DEFAULT_VOICES];

    // Filter by gender
    if (filters.gender && filters.gender !== 'all') {
      result = result.filter(v => v.gender === filters.gender);
    }

    // Filter by use case
    if (filters.useCase && filters.useCase !== 'all') {
      result = result.filter(v => v.useCase === filters.useCase);
    }

    // Filter by search query
    if (filters.searchQuery && filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase().trim();
      result = result.filter(
        v =>
          v.name.toLowerCase().includes(query) ||
          v.description.toLowerCase().includes(query) ||
          v.accent.toLowerCase().includes(query)
      );
    }

    return result;
  }, [filters]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const selectVoice = useCallback((voiceId: string | null) => {
    setSelectedVoiceId(voiceId);
  }, []);

  const setFilters = useCallback((newFilters: Partial<typeof DEFAULT_FILTERS>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  // ============================================================================
  // Return API
  // ============================================================================

  return {
    voices: DEFAULT_VOICES as IDefaultVoice[],
    filteredVoices,
    selectedVoiceId,
    filters,
    femaleVoices,
    maleVoices,
    getVoiceById: getDefaultVoiceById,
    isDefaultVoice,
    selectVoice,
    setFilters,
    resetFilters,
  };
}
